/**
 * Roles Tab Component
 *
 * Displays list of roles with CRUD operations
 * Features: DataTable, search, create, edit, delete, assign rights
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconShield, IconEdit, IconKey, IconTrash, IconPlus } from '@tabler/icons-react';
import { Stack, Group, Button, Text, Badge, ActionIcon } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { alert } from '../../../../components/ui/providers/AlertProvider';
import type { RolesTabProps, RoleRow } from '../types';

export function RolesTab({ roles, onRefresh, onEdit, onDelete, loading }: RolesTabProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Format date helper
   */
  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Handle assign rights navigation
   */
  const handleAssignRights = useCallback(
    (roleId: string) => {
      navigate(`/admin/permissions/roles/${roleId}/assign`);
    },
    [navigate]
  );

  /**
   * Handle delete with confirmation
   */
  const handleDeleteClick = useCallback(
    async (role: RoleRow) => {
      const message =
        role.userCount > 0
          ? `This role is assigned to ${role.userCount} user(s). Deleting it will remove it from all users. Are you sure you want to delete "${role.name}"?`
          : `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`;

      const confirmed = await alert.confirm('Delete Role', message);
      if (!confirmed) return;

      onDelete(role);
    },
    [onDelete]
  );

  /**
   * Filter roles by search query
   */
  const filteredRoles = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') return roles;

    const query = searchQuery.toLowerCase().trim();
    return roles.filter((role) => {
      const name = (role.name || '').toLowerCase();
      const description = (role.description || '').toLowerCase();
      return name.includes(query) || description.includes(query);
    });
  }, [roles, searchQuery]);

  /**
   * Convert to DataTable row format
   */
  const roleRows: RoleRow[] = useMemo(() => {
    return filteredRoles.map((role) => ({
      ...role,
      id: role.id, // Ensure id is non-nullable
    }));
  }, [filteredRoles]);

  /**
   * Define DataTable columns
   */
  const columns: DataTableColumn<RoleRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Role Name',
        sortable: true,
        render: (value, role) => (
          <Group gap="xs">
            <IconShield size={16} style={{ opacity: 0.6 }} />
            <Text fw={500} size="sm">
              {value}
            </Text>
          </Group>
        ),
      },
      {
        key: 'description',
        label: 'Description',
        render: (value) => (
          <Text size="sm" c="dimmed">
            {value || '-'}
          </Text>
        ),
      },
      {
        key: 'userCount',
        label: 'Users',
        sortable: true,
        render: (value) => (
          <Badge size="sm" variant="light" color="blue">
            {value}
          </Badge>
        ),
      },
      {
        key: 'permissions',
        label: 'Rights',
        sortable: true,
        render: (value: string[]) => (
          <Badge size="sm" variant="light" color="cyan">
            {value?.length || 0}
          </Badge>
        ),
      },
      {
        key: 'createdDate',
        label: 'Created',
        sortable: true,
        render: (value) => (
          <Text size="sm" c="dimmed">
            {formatDate(value)}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, role) => (
          <Group gap="xs" justify="flex-end">
            <ActionIcon
              variant="light"
              size="sm"
              color="gray"
              onClick={() => onEdit(role)}
              aria-label="Edit role"
            >
              <IconEdit size={14} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              size="sm"
              color="blue"
              onClick={() => handleAssignRights(role.id)}
              aria-label="Assign rights"
            >
              <IconKey size={14} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              size="sm"
              color="red"
              onClick={() => handleDeleteClick(role)}
              aria-label="Delete role"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onEdit, handleAssignRights, handleDeleteClick]
  );

  return (
    <Stack gap="md">
      <DataTable<RoleRow>
        data={roleRows}
        columns={columns}
        searchPlaceholder="Search roles by name or description..."
        searchValue={searchQuery}
        onSearch={(query) => setSearchQuery(query)}
        gradientVariant="primary"
        highlightOnHover
        striped
        withRowAnimations
        enableInternalSort
        emptyMessage={loading ? 'Loading roles...' : 'No roles found'}
        loading={loading}
      />
    </Stack>
  );
}
